import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { useEffect } from 'react';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { InventoryProvider } from './contexts/InventoryContext';
import { SafeAreasProvider } from './contexts/SafeAreasContext';
import { MainLayout } from './layout/MainLayout';
import { Dashboard } from './pages/Dashboard';
import { SettingsPage } from './pages/SettingsPage';
import { EventsPage } from './pages/EventsPage';
import { GuestsPage } from './pages/GuestsPage';
import { GuestDetailsPage } from './pages/GuestDetailsPage';
import { EventDetailsPage } from './pages/EventDetailsPage';
import { GeneralSettingsPage } from './pages/GeneralSettingsPage';
import { InventoryDetail } from './pages/InventoryDetail';
import { InventoryForm } from './pages/InventoryForm';
import PricingTable from './pages/externalPages/pricingPage';
import PrivacyPolicy from './pages/externalPages/privacyPolicyPage';
import TermsOfUsePage from './pages/externalPages/termsOfUsePage';
import LandingPage from './pages/externalPages/landingPage';
import ChildSafetyPage from './pages/externalPages/childSafetyPage';
import HostlyLogin from './pages/login';
import { usePushNotifications } from './hooks/usePushNotifications';
import { LoadingSpinner } from './components/LoadingSpinner';
import { TodoListPage } from './pages/todoListPage';
import { HostlyClubPage } from './pages/hostlyClubPage';
import { HostlyAIPage } from './pages/HostlyAIPage';
import { NotificationsPage } from './pages/notificationsPage';
import { WishlistPage } from './pages/wishlistPage';
import { ShoppingListsPage } from './pages/ShoppingListsPage';
import { InventoryReportsPage } from './pages/InventoryReportsPage';
import { BrowserHistory } from './pages/inventoryUseHistory';
import FavoriteItemsPage from './pages/favoriteItemsPage';
import { RecipesPage } from './pages/recipesPage';
import { PortionCalculatorPage } from './pages/PortionCalculatorPage';
import { Inventory } from './pages/Inventory';
import { SharedInventoryPage } from './pages/SharedInventoryPage';
import { RecipeDetailsPage } from './pages/recipeDetailsPage';
import { RecipeSharePage } from './pages/RecipeSharePage';
import { RecipeExplorePage } from './pages/RecipeExplorePage';
import { TodoHistoryPage } from './pages/TodoHistoryPage';
import { CalendarPage } from './pages/CalendarPage';
import { AdminPanel } from './pages/AdminPanel';
import { ContactPage } from './pages/ContactPage';
import { PrivacyRequestPage } from './pages/PrivacyRequestPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import TrialExpiredPage from './pages/TrialExpiredPage';
import { TrialExpiredRedirect } from './components/TrialExpiredRedirect';
import { UserProfilePage } from './pages/UserProfilePage';
import { DiscoverUsersPage } from './pages/DiscoverUsersPage';
import { PostDetailsPage } from './pages/PostDetailsPage';
import { MesaVirtualPage } from './pages/MesaVirtualPage';

function ClarityPageTracker() {
  const location = useLocation();

  useEffect(() => {
    if (window.clarity) {
      window.clarity('set', 'currentPage', location.pathname);
      window.clarity('event', 'pageview');
    }
  }, [location]);

  return null;
}

function AppContent() {
  const { user, loading } = useAuth();
  const isWeb = !Capacitor.isNativePlatform();

  usePushNotifications();

  if (loading && !isWeb) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <Router>
      <ClarityPageTracker />
      <TrialExpiredRedirect />
      <Routes>
        {/* Rotas públicas - sempre disponíveis */}
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingTable />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/child-safety" element={<ChildSafetyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy-request" element={<PrivacyRequestPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="/trial-expired" element={user ? <TrialExpiredPage /> : <Navigate to="/login" replace />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <HostlyLogin />} />

        {/* Rota raiz: web sempre landing, app vai pro dashboard ou login */}
        <Route
          path="/"
          element={
            isWeb ? (
              <LandingPage />
            ) : (
              user ? <MainLayout><Dashboard /></MainLayout> : <HostlyLogin />
            )
          }
        />

        {/* Rotas autenticadas - sempre disponíveis, mas redirecionam se não logado */}
        <Route
          path="/dashboard"
          element={user ? <MainLayout><Dashboard /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/settings"
          element={user ? <MainLayout><SettingsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/events"
          element={user ? <MainLayout><EventsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/events/:eventId"
          element={user ? <MainLayout><EventDetailsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/guests"
          element={user ? <MainLayout><GuestsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/guests/:guestId"
          element={user ? <MainLayout><GuestDetailsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/general-settings"
          element={user ? <MainLayout><GeneralSettingsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/inventory"
          element={user ? <MainLayout><Inventory /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/inventory/reports"
          element={user ? <MainLayout><InventoryReportsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/inventory/new"
          element={user ? <MainLayout><InventoryForm /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/inventory/edit/:itemId"
          element={user ? <MainLayout><InventoryForm /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/inventory/:itemId"
          element={user ? <MainLayout><InventoryDetail /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/inventory/:itemId/history"
          element={user ? <MainLayout><BrowserHistory /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/inventory/shared/:ownerId"
          element={user ? <MainLayout><SharedInventoryPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/todo-list"
          element={user ? <MainLayout><TodoListPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/hostly-club"
          element={user ? <MainLayout><HostlyClubPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/hostly-ai"
          element={user ? <MainLayout><HostlyAIPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/notifications"
          element={user ? <MainLayout><NotificationsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/notifications/recipe-share"
          element={user ? <MainLayout><RecipeSharePage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/wishlist"
          element={user ? <MainLayout><WishlistPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/shopping-lists"
          element={user ? <MainLayout><ShoppingListsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/browser-history"
          element={user ? <MainLayout><BrowserHistory /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/favorite-items"
          element={user ? <MainLayout><FavoriteItemsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/recipes"
          element={user ? <MainLayout><RecipesPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/mesa"
          element={user ? <MainLayout><MesaVirtualPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/recipes/calculator"
          element={user ? <MainLayout><PortionCalculatorPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/recipes/explore"
          element={user ? <MainLayout><RecipeExplorePage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/recipes/:recipeId"
          element={user ? <MainLayout><RecipeDetailsPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/todos"
          element={user ? <MainLayout><TodoListPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/todos/history"
          element={user ? <MainLayout><TodoHistoryPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/calendar"
          element={user ? <MainLayout><CalendarPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/admin"
          element={<AdminPanel />}
        />
        <Route
          path="/hostlyclub/profile/:userId"
          element={user ? <MainLayout><UserProfilePage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/hostlyclub/discover"
          element={user ? <MainLayout><DiscoverUsersPage /></MainLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/hostlyclub/post/:postId"
          element={user ? <MainLayout><PostDetailsPage /></MainLayout> : <Navigate to="/login" replace />}
        />

        {/* Catch-all: rotas não encontradas */}
        <Route path="*" element={<Navigate to={isWeb ? "/landing" : "/"} replace />} />
      </Routes>
    </Router>
  );
}

const App: React.FC = () => {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <InventoryProvider>
          <ThemeProvider>
            <SafeAreasProvider>
              <CssBaseline />
              <GlobalStyles
                styles={{
                  '@media (max-width: 767px)': {
                    'input, select, textarea, .MuiInputBase-input': { fontSize: '16px' },
                  },
                }}
              />
              <AppContent />
            </SafeAreasProvider>
          </ThemeProvider>
        </InventoryProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
};

export default App;
